import React from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {Button, List} from 'antd';
import {CarOutlined, PlusOutlined} from '@ant-design/icons';

import ListCard from '../../../components/ListCard';
import PageTitle from '../../../components/page/PageTitle';
import ErrorBoundary from '../../../components/ErrorBoundary';
import {getCarsLocations} from '../../../helpers/car';
import {messageType, showMessages} from '../../../helpers/message';
import {listGridStyle} from '../../../assets/styles';
import carPlaceholderImage from '../../../assets/car-placeholder.png';

import "./CarsViewStaff.less";

const CarsViewStaff = () => {
    const abortControllerRef = React.useRef(new AbortController());
    const abortSignalRef = React.useRef(abortControllerRef.current.signal);
    const navigate = useNavigate();
    const location = useLocation();

    const [cars, setCars] = React.useState([]);
    const [loading, setLoading] = React.useState(false);

    const [page, setPage] = React.useState(location.state && location.state.page ? location.state.page : 1);
    const [perPage, setPerPage] = React.useState(location.state && location.state.perPage ? location.state.perPage : 10);
    const [total, setTotal] = React.useState(10);

    React.useEffect(() => {
        const fetchCars = async (page, perPage) => {
            const abortSignal = abortSignalRef.current;
            setLoading(true);

            try {
                const response = await getCarsLocations(page, perPage, 'number', abortSignal);

                if (abortSignal.aborted === false && response) {
                    setCars(response.data);
                    setTotal(response.total);
                    setLoading(false);
                }

            } catch (error) {
                showMessages(error.message, messageType.error);
                setLoading(false);
                if (error.status_code === 404) window.location.replace('/404');
            }
        };

        fetchCars(page, perPage);
    }, [page, perPage]);

    React.useEffect(() => {
        const abortController = abortControllerRef.current;
        // Clears the state upon page refresh/reload.
        window.history.replaceState(null, '');
        return () => {
            abortController.abort();
        }
    }, []);

    React.useEffect(() => {
        const {state} = location;
        if (state && state.page && state.perPage) {
            setPage(state.page);
            setPerPage(state.perPage);
        }
    }, [location]);

    const onClick = (id) => {
        navigate(`/fleet/car?id=${id}`, {
            state: {page, perPage}
        });
    };

    const pageChange = (page, perPage) => {
        setPage(page);
        setPerPage(perPage);
    };

    const paginationAttr = {
        onChange: pageChange,
        showSizeChanger: true,
        onShowSizeChange: pageChange,
        total,
        current: page,
        pageSize: perPage
    };

    return (
        <ErrorBoundary>
            <div className="vehicles-header">
                <PageTitle title="All Vehicles" icon={<CarOutlined/>}/>
                <Button
                    className="new-vehicle-button"
                    type="default"
                    icon={<PlusOutlined/>}
                    onClick={() => navigate('/fleet/car/add')}
                >
                    New Vehicle
                </Button>
            </div>
            <List className="list-pagination-bottom cars-list"
                  grid={{...listGridStyle}}
                  dataSource={cars}
                  loading={loading}
                  pagination={paginationAttr}
                  renderItem={
                      car => {
                          const title = car.deployable_description || 'N/A';
                          const description = car.location_description || 'No current location.';
                          const imageDescription = 'Vehicle #' + car.number;

                          return <List.Item
                              key={car.id}
                          >
                              <ListCard
                                  buttonText="View Vehicle"
                                  id={car.id}
                                  title={title}
                                  description={description}
                                  onClick={onClick}
                                  image={car.image ? car.image : carPlaceholderImage}
                                  imageAltText="car"
                                  imageDescription={imageDescription}
                              />
                          </List.Item>
                      }
                  }
            />
        </ErrorBoundary>
    );
}

CarsViewStaff.propTypes = {};

export default CarsViewStaff;
